import styled from "styled-components";
import {fontFamilies, radii} from "theme/tokens";

/**
 * Externalized styles for {@link ./PageThemeSection}, the download-page theme editor (api#511).
 *
 * Deliberately SHORT: the template cards, the clipped preview stage and the placeholder art all come
 * from `sponsor/BackdropTemplatePicker.styles`, and the upsell block, hints, hidden file input and the
 * section separator/heading from `./BrandSettings.styles`. Only what this section adds on top of those
 * lives here — the image tiles, the live-state block and the two category headings. A third preview
 * style would be a third thing to keep in sync with the renderer it is supposed to be previewing, so the
 * wallpaper stage comes from `sponsor/options/WallpaperPreview.styles` for the same reason.
 */

/** One labelled category of treatments (kitecorp/transferify-ui#1869). */
export const CategoryGroup = styled.div`
	margin-top: 14px;
`;

/**
 * An `h4`, not an `h2`.
 *
 * This editor is a SECTION of the branding sheet, whose own title is the `h3` `SectionTitle` above it,
 * so the level below that is what keeps a reader navigating by heading from meeting "Full page" under
 * nothing. The sponsor page reaches a different answer for the same two words because its own heading
 * ladder starts higher.
 */
export const CategoryName = styled.h4`
	margin: 0;
	font-size: 13px;
	font-weight: 700;
	letter-spacing: -0.01em;
	color: ${({theme}) => theme.tokens.ink};
`;

export const CategoryLede = styled.p`
	margin: 3px 0 10px;
	font-size: 12px;
	line-height: 1.5;
	color: ${({theme}) => theme.tokens.ink2};
`;

/**
 * The full-page settings, under the picker that opened them.
 *
 * A column rather than a side-by-side pair: this editor lives inside the branding SHEET, which is
 * narrower than the booking page's content column at every width the sheet opens at, so the live stage
 * sits above the controls that change it instead of beside them.
 */
export const WallpaperOptions = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	margin-top: 16px;
`;

/** The chosen images, in the order they animate. Scrolls sideways rather than reflowing the sheet. */
export const ImageRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
	align-items: stretch;
`;

export const ImageTile = styled.div`
	position: relative;
	width: 108px;
	display: flex;
	flex-direction: column;
	gap: 6px;
`;

/**
 * An image the sender owns but the draft theme leaves out (api#471).
 *
 * Dimmed rather than hidden or moved to a second list: it is still the sender's image, and it has to stay
 * one click from being used again. The state is ALSO named in words on {@link TileState} — opacity alone
 * would be invisible to a screen reader and unreliable for anyone who cannot compare two shades.
 */
export const TileImage = styled.img<{$inTheme: boolean}>`
	width: 108px;
	aspect-ratio: 16 / 10;
	object-fit: cover;
	border-radius: ${radii.control};
	border: 1px solid ${({theme, $inTheme}) => ($inTheme ? theme.tokens.selectedEdge : theme.tokens.cardBorder)};
	background: ${({theme}) => theme.tokens.field};
	opacity: ${({$inTheme}) => ($inTheme ? 1 : 0.45)};
`;

/** The tile's state in words, under the image. Reads in both themes because it inherits ink tokens. */
export const TileState = styled.span<{$inTheme: boolean}>`
	font-family: ${fontFamilies.mono};
	font-size: 10px;
	font-weight: 700;
	letter-spacing: 0.04em;
	color: ${({theme, $inTheme}) => ($inTheme ? theme.tokens.linkInk : theme.tokens.ink3)};
	overflow-wrap: anywhere;
`;

export const TileControls = styled.div`
	display: flex;
	justify-content: space-between;
	gap: 4px;
`;

export const TileButton = styled.button.attrs({type: "button"})`
	width: 30px;
	height: 26px;
	border-radius: ${radii.control};
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	background: ${({theme}) => theme.tokens.field};
	color: ${({theme}) => theme.tokens.ink2};
	font-size: 13px;
	line-height: 1;
	cursor: pointer;

	&:hover:not(:disabled) {
		border-color: ${({theme}) => theme.tokens.selectedEdge};
		color: ${({theme}) => theme.tokens.ink};
	}

	&:disabled {
		opacity: 0.45;
		cursor: not-allowed;
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

/**
 * The "add" affordance. A `<label>` around a visually hidden `<input type="file">`, so the whole tile is
 * the click target and the input keeps its native picker, keyboard focus and accessible name.
 */
export const AddImageLabel = styled.label<{$disabled: boolean}>`
	position: relative;
	width: 108px;
	aspect-ratio: 16 / 10;
	display: flex;
	align-items: center;
	justify-content: center;
	text-align: center;
	padding: 6px;
	box-sizing: border-box;
	border-radius: ${radii.control};
	border: 1.5px dashed ${({theme}) => theme.tokens.cardBorder};
	background: ${({theme}) => theme.tokens.field};
	color: ${({theme}) => theme.tokens.ink2};
	font-size: 12px;
	font-weight: 700;
	cursor: ${({$disabled}) => ($disabled ? "not-allowed" : "pointer")};
	opacity: ${({$disabled}) => ($disabled ? 0.5 : 1)};

	&:hover {
		border-color: ${({theme, $disabled}) => ($disabled ? theme.tokens.cardBorder : theme.tokens.selectedEdge)};
	}

	&:has(input:focus-visible) {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

export const Counter = styled.span`
	align-self: flex-start;
	padding: 2px 8px;
	border-radius: ${radii.chip};
	background: ${({theme}) => theme.tokens.chip};
	color: ${({theme}) => theme.tokens.ink2};
	font-family: ${fontFamilies.mono};
	font-size: 11px;
	font-weight: 700;
`;

/** What the saved theme is doing right now, before any edit in this session. */
export const LiveState = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	padding: 10px 12px;
	border-radius: ${radii.control};
	background: ${({theme}) => theme.tokens.field};
	border: 1px solid ${({theme}) => theme.tokens.line};
`;

export const LiveLabel = styled.span`
	font-family: ${fontFamilies.mono};
	font-size: 10px;
	font-weight: 700;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: ${({theme}) => theme.tokens.ink3};
`;

export const LiveValue = styled.span`
	font-size: 13px;
	font-weight: 700;
	color: ${({theme}) => theme.tokens.ink};
`;

/** The enable/disable row: label and hint on the left, the shared pill switch on the right. */
export const ToggleRow = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`;

export const ToggleCopy = styled.div`
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 2px;
`;

export const ToggleName = styled.span`
	font-size: 13px;
	font-weight: 700;
	color: ${({theme}) => theme.tokens.ink};
`;

